import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import type { WebCurrency } from '@lc/contracts';
import { api, API_URL } from '../api';
import { formatDate, formatPrice } from '../format';
import { useI18n, type Key } from '../i18n';
import { useLoad, useShopEntry } from '../session';
import { Halftone, Petal, Plate, Splat, Wordmark } from '../ui/chrome';
import { CheckGlyph, CrownGlyph, LogoutGlyph } from '../ui/glyphs';

/** A media path from the API (`/media/…`) as a URL this site can load. */
const mediaUrl = (u: string | null | undefined) => (!u ? null : /^https?:/.test(u) ? u : `${new URL(API_URL).origin}${u.startsWith('/api/') ? '' : '/api/v1'}${u}`);

/** 帳戶: who you are, your balance, Plus (managed in Stripe's portal) and what you've bought. */
export function AccountPage() {
  const { t, lang } = useI18n();
  const shopEntry = useShopEntry();
  const navigate = useNavigate();
  const me = useLoad(() => api.me(), []);
  const character = useLoad(() => api.character().catch(() => null), []);
  const wallet = useLoad(() => api.wallet(), []);
  const history = useLoad(() => api.history(), [lang]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const manage = async () => {
    setBusy(true);
    setError(null);
    try {
      window.location.assign((await api.portal()).url);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error.generic'));
      setBusy(false);
    }
  };
  const signOut = async () => {
    await api.signOut();
    navigate('/', { replace: true });
  };

  const name = character.data?.name ?? me.data?.displayName ?? '';
  const portrait = mediaUrl(character.data?.portraitUrl);
  const plus = wallet.data?.plus;
  const plan = { md_plus_weekly: 'week', md_plus_monthly: 'month', md_plus_yearly: 'year' }[plus?.productId ?? ''];
  // Purchases only (a pack's first-purchase bonus is part of it), newest first, as many as fit the card.
  const rows = (history.data ?? []).filter((r) => r.kind !== 'reward').slice(0, 4);
  return (
    <div className="account">
      <div className="account-bg" aria-hidden />
      <Splat n={1} style={{ left: -70, top: 300, width: 260, zIndex: 1 }} />
      <Halftone style={{ left: 0, top: 330, width: 160, height: 200, zIndex: 1 }} />
      <Splat n={3} style={{ left: -40, bottom: 20, width: 220, zIndex: 5, wide: true }} />
      <Petal n={1} style={{ left: 580, top: 230, width: 56, zIndex: 3, wide: true }} />
      <Petal n={2} style={{ right: 20, top: 40, width: 60, zIndex: 3, wide: true }} />
      <Petal n={1} style={{ left: 300, bottom: 16, width: 70, zIndex: 5, wide: true }} />
      <div className="account-head">
        <Link to="/" className="account-logo" aria-label={t('nav.home')}>
          <Wordmark />
          <span className="tag">— {t('account.tagline')} —</span>
        </Link>
        <img className="account-lineup" src="/art/lineup.webp" alt="" width={1400} height={1033} />
        <div className="account-title">
          <Plate as="h1" large petal>
            {t('account.title')}
          </Plate>
        </div>
      </div>
      <div className="card account-panel">
        <section>
          <div className="who-head">
            <div className="avatar" aria-hidden style={portrait ? { backgroundImage: `url(${portrait})` } : undefined}>
              {portrait ? null : name.slice(0, 1).toUpperCase() || '✿'}
            </div>
            <div style={{ minWidth: 0 }}>
              <h2>{name || t('account.title')}</h2>
              {me.data?.email ? <p>{me.data.email}</p> : null}
            </div>
          </div>
          <div className="balance">
            <div className="stat">
              <span className="ico">
                <img src="/art/pack_1.webp" alt="" />
              </span>
              <span>{t('account.credits')}</span>
              <b>{wallet.data ? wallet.data.credits : '–'}</b>
            </div>
            <div className="stat">
              <span className="ico">
                <img src="/art/pack_7.webp" alt="" />
              </span>
              <span>{t('account.coins')}</span>
              <b className="coins">{wallet.data ? wallet.data.coins.toLocaleString() : '–'}</b>
            </div>
          </div>
          <div className="plus-row">
            <CrownGlyph />
            <div className="grow">
              <h3>Inked Plus</h3>
              <p>
                {plus?.active ? (
                  <>
                    {plan ? `${t(`plan.${plan}` as Key)} · ` : ''}
                    {plus.renewsAt ? (
                      <>
                        {t('account.renewsOn')} <em>{formatDate(plus.renewsAt, lang, true)}</em>
                      </>
                    ) : null}
                  </>
                ) : (
                  t('account.plusOff')
                )}
              </p>
            </div>
            {plus?.active ? (
              plus.source === 'stripe' ? (
                <button className="btn btn-pink" disabled={busy} onClick={() => void manage()}>
                  {t('account.manage')}
                </button>
              ) : null
            ) : shopEntry ? (
              <button className="btn btn-pink" onClick={() => navigate('/shop')}>
                {t('nav.shop')}
              </button>
            ) : null}
          </div>
          {plus?.active && plus.source === 'store' ? <p className="muted" style={{ marginTop: 12 }}>{t('account.manageAppStore')}</p> : null}
          {plus?.alsoBilledBy ? <p className="error" style={{ marginTop: 12 }}>{t('account.doubleBilled')}</p> : null}
          {error ? <p className="error" style={{ marginTop: 12 }}>{error}</p> : null}
        </section>
        <section>
          <div className="history-card">
            <Plate>{t('account.history')}</Plate>
            {history.data && history.data.length === 0 ? <p className="muted">{t('account.empty')}</p> : null}
            {history.data?.length ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('account.date')}</th>
                    <th>{t('account.item')}</th>
                    <th>{t('account.paid')}</th>
                    <th className="col-status">{t('account.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(r.createdAt, lang, true)}</td>
                      <td>
                        {r.reason}
                        {r.environment === 'sandbox' ? <span className="test-tag">{t('account.test')}</span> : null}
                      </td>
                      <td className="amt">{r.paid ? `${r.kind === 'refund' ? '−' : ''}${formatPrice(r.paid.amount, r.paid.currency as WebCurrency, lang)}` : '—'}</td>
                      <td className="col-status">
                        <span className={`status${r.kind === 'refund' ? ' refund' : ''}`}>
                          {r.kind === 'refund' ? null : <CheckGlyph />}
                          {t(r.kind === 'refund' ? 'account.refunded' : 'account.done')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            <div className="logout">
              <button className="btn btn-ghost" onClick={() => void signOut()}>
                <LogoutGlyph />
                {t('account.logout')}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
